import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Youtube, Instagram, Linkedin, Facebook, Mic } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Magnetic } from "@/components/animations/ScrollAnimations";
import TikTokIcon from "@/components/icons/TikTokIcon";
import SpotifyIcon from "@/components/icons/SpotifyIcon";
import logo from "@/assets/logo.webp";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Episodes", path: "/episodes" },
    { name: "Blog", path: "/blog" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Be Our Guest", path: "/apply", to: "/apply#application-form", featured: true },
    { name: "Guest Sign In", path: "/guest-signin" },
  ];

  const isActive = (path: string) => location.pathname === path;


  const menuVariants = {
    closed: {
      opacity: 0,
      transition: {
        duration: 0.3,
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
    open: {
      opacity: 1,
      transition: {
        duration: 0.3,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    closed: { opacity: 0, y: 30 },
    open: { opacity: 1, y: 0 },
  };

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-background/90 backdrop-blur-xl border-b border-border/50"
          : "bg-transparent"
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
    >
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Link to="/" className="relative z-10 block" aria-label="Go to homepage">
              <motion.img
                src={logo}
                alt="The Founders Lounge"
                width={178}
                height={100}
                className="h-8 md:h-10 w-auto"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <motion.div
            className="hidden lg:flex items-center gap-4 xl:gap-6"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {navLinks.filter((link) => !link.featured).map((link) => (
              <Link
                key={link.path}
                to={link.to ?? link.path}
                className="relative group"
              >
                <motion.span
                  className={`font-display text-sm uppercase tracking-[0.15em] whitespace-nowrap transition-colors duration-300 ${
                    isActive(link.path.split("#")[0])
                      ? "text-primary"
                      : "text-foreground/70 group-hover:text-foreground"
                  }`}
                  whileHover={{ y: -2 }}
                  transition={{ duration: 0.2 }}
                >
                  {link.name}
                </motion.span>
                <motion.span
                  className="absolute -bottom-1 left-0 h-0.5 bg-primary"
                  initial={{ width: isActive(link.path.split("#")[0]) ? "100%" : "0%" }}
                  animate={{ width: isActive(link.path.split("#")[0]) ? "100%" : "0%" }}
                  whileHover={{ width: "100%" }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            ))}

          </motion.div>

          {/* Desktop CTA & Socials */}
          <motion.div
            className="hidden lg:flex items-center gap-6"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="flex items-center gap-4">
              {[
                { icon: Youtube, href: "https://www.youtube.com/@theFoundersLoungePodcast", label: "YouTube" },
                { icon: Instagram, href: "https://www.instagram.com/thefoundersloungepodcast/", label: "Instagram" },
                { icon: Facebook, href: "https://www.facebook.com/thefoundersloungepodcast/", label: "Facebook" },
                { icon: Linkedin, href: "https://www.linkedin.com/company/the-founders-lounge-podcast/", label: "LinkedIn" },
                { icon: TikTokIcon, href: "https://www.tiktok.com/@thefoundersloungepodcast", label: "TikTok" },
                { icon: SpotifyIcon, href: "https://open.spotify.com/show/2h6PHEsBTYNyhy3Zcv2Oai", label: "Spotify" },
              ].map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="text-foreground/50 hover:text-primary transition-colors duration-300"
                  whileHover={{ scale: 1.2, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
            <Magnetic strength={0.3}>
              <motion.div
                animate={{ scale: [1, 1.04, 1] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              >
                <Button variant="hero" size="sm" className="shadow-[0_0_24px_hsl(var(--primary)/0.45)]" asChild>
                  <Link to="/apply#application-form">
                    <Mic className="w-4 h-4" />
                    Be Our Guest
                  </Link>
                </Button>
              </motion.div>
            </Magnetic>
            <Button variant="outline" size="sm" asChild>
              <a href="https://www.youtube.com/@theFoundersLoungePodcast" target="_blank" rel="noopener noreferrer">
                Subscribe
              </a>
            </Button>

          </motion.div>

          {/* Tablet & Mobile Actions */}
          <div className="lg:hidden flex items-center gap-3 relative z-[60]">
            {/* Tablet: Be Our Guest pill right in the header */}
            <Link
              to="/apply#application-form"
              aria-label="Be Our Guest, go to the application form"
              className="hidden md:inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-primary/60 bg-primary/15 px-4 py-2 font-display text-xs uppercase tracking-[0.15em] text-primary shadow-[0_0_22px_hsl(var(--primary)/0.4)] transition-transform duration-200 hover:scale-105 active:scale-95"
            >
              <Mic className="w-4 h-4" />
              Be Our Guest
            </Link>
            <motion.button
              className="relative w-10 h-10 flex items-center justify-center"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            whileTap={{ scale: 0.9 }}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
          >
            <AnimatePresence mode="wait">
              {isMobileMenuOpen ? (
                <motion.div
                  key="close"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <X className="w-6 h-6 text-foreground" />
                </motion.div>
              ) : (
                <motion.div
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <Menu className="w-6 h-6 text-foreground" />
                </motion.div>
              )}
            </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="lg:hidden fixed inset-0 z-[55] bg-background"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-1/4 right-0 w-64 h-64 rounded-full bg-primary/20 blur-[100px]" />
              <div className="absolute bottom-1/4 left-0 w-64 h-64 rounded-full bg-primary/10 blur-[100px]" />
            </div>

            <motion.div
              className="flex flex-col items-center justify-center h-full gap-8 relative z-10"
              variants={menuVariants}
              initial="closed"
              animate="open"
              exit="closed"
            >
              {navLinks.map((link) => (
                <motion.div key={link.path} variants={itemVariants}>
                  <Link
                    to={link.to ?? link.path}
                    className={
                      link.featured
                        ? "inline-flex items-center gap-3 rounded-full border border-primary/60 bg-primary/15 px-6 py-3 font-display text-3xl uppercase tracking-widest text-primary shadow-[0_0_30px_hsl(var(--primary)/0.35)]"
                        : `font-display text-4xl uppercase tracking-widest transition-colors ${
                            isActive(link.path) ? "text-primary" : "text-foreground"
                          }`
                    }
                  >
                    {link.featured && <Mic className="w-7 h-7" />}
                    {link.name}
                  </Link>
                </motion.div>
              ))}


              <motion.div
                className="flex items-center gap-8 mt-8"
                variants={itemVariants}
              >
              {[
                { icon: Youtube, href: "https://www.youtube.com/@theFoundersLoungePodcast", label: "YouTube" },
                { icon: Instagram, href: "https://www.instagram.com/thefoundersloungepodcast/", label: "Instagram" },
                { icon: Facebook, href: "https://www.facebook.com/thefoundersloungepodcast/", label: "Facebook" },
                { icon: Linkedin, href: "https://www.linkedin.com/company/the-founders-lounge-podcast/", label: "LinkedIn" },
                { icon: TikTokIcon, href: "https://www.tiktok.com/@thefoundersloungepodcast", label: "TikTok" },
                { icon: SpotifyIcon, href: "https://open.spotify.com/show/2h6PHEsBTYNyhy3Zcv2Oai", label: "Spotify" },
               ].map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="text-foreground/60 hover:text-primary transition-colors"
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <social.icon className="w-8 h-8" />
                  </motion.a>
                ))}
              </motion.div>

              <motion.div variants={itemVariants}>
                <Button variant="hero" size="lg" className="mt-4" asChild>
                  <a href="https://www.youtube.com/@theFoundersLoungePodcast" target="_blank" rel="noopener noreferrer">
                    Subscribe Now
                  </a>
                </Button>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
